(() => {
  const STATE_LABELS = {
    AGU: 'Aguascalientes', BCN: 'Baja California', BCS: 'Baja California Sur', CAM: 'Campeche', CHP: 'Chiapas',
    CHH: 'Chihuahua', CMX: 'CDMX', COA: 'Coahuila', COL: 'Colima', DUR: 'Durango', GUA: 'Guanajuato',
    GRO: 'Guerrero', HID: 'Hidalgo', JAL: 'Jalisco', MEX: 'Estado de México', MIC: 'Michoacán', MOR: 'Morelos',
    NAY: 'Nayarit', NLE: 'Nuevo León', PUE: 'Puebla', QUE: 'Querétaro', ROO: 'Quintana Roo', SIN: 'Sinaloa',
    SLP: 'San Luis Potosí', SON: 'Sonora', TAB: 'Tabasco', TAM: 'Tamaulipas', TLA: 'Tlaxcala', VER: 'Veracruz',
    YUC: 'Yucatán', ZAC: 'Zacatecas'
  };

  // Correcciones conocidas del archivo legado. Mantener aquí hasta que la fuente se limpie.
  const STORE_OVERRIDES = {
    T112: { stateCode: 'BCN' }, // Tijuana
    T117: { stateCode: 'MEX' }, // Coacalco
    T107: { stateCode: 'MEX' }  // Valle de Chalco
  };

  const normalizeText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const cleanDisplayName = (rawName = '') => {
    const parts = String(rawName).split(',').map((part) => part.trim()).filter(Boolean);
    const candidate = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '';
    return candidate
      .toLowerCase()
      .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
  };

  const normalizeZip = (zip) => String(zip ?? '').replace(/\D/g, '').padStart(5, '0').slice(-5);

  function normalizeStore(raw) {
    const storeCode = String(raw.storeCode || '').trim();
    const override = STORE_OVERRIDES[storeCode] || {};
    const latitude = Number.parseFloat(raw.lat);
    const longitude = Number.parseFloat(raw.lng);
    const stateCode = override.stateCode || String(raw.estado || '').trim().toUpperCase();

    return {
      raw,
      id: raw.id,
      storeCode,
      name: cleanDisplayName(raw.name),
      displayName: fixEncoding(raw.name),
      city: String(raw.ciudad || '').trim(),
      municipality: String(raw.municipio || '').trim(),
      address1: String(raw.address || '').trim(),
      address2: String(raw.addressTwo || '').trim(),
      stateCode,
      state: STATE_LABELS[stateCode] || stateCode,
      postalCode: normalizeZip(raw.zipCode),
      phone: raw.phone ? String(raw.phone) : '',
      website: String(raw.website || '').trim(),
      email: String(raw.email || '').trim(),
      latitude,
      longitude,
      hasValidCoordinates: Number.isFinite(latitude) && Number.isFinite(longitude),
      schedule: {
        monday: raw.monday || '', tuesday: raw.tuesday || '', wednesday: raw.wednesday || '',
        thursday: raw.thursday || '', friday: raw.friday || '', saturday: raw.saturday || '', sunday: raw.sunday || ''
      }
    };
  }

  function fixEncoding(value) {
    if (typeof value !== 'string') return value;

    if (!/[ÃÂ]/.test(value)) {
      return value;
    }

    try {
      return decodeURIComponent(
        Array.from(value)
          .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
    } catch {
      return value;
    }
  }

  function getStores() {
    const source = Array.isArray(window.shasaStores) ? window.shasaStores : [];
    return source.map(normalizeStore).filter((store) => store.storeCode && store.hasValidCoordinates);
  }

  function haversineKm(a, b) {
    const R = 6371;
    const toRad = (deg) => deg * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function storesNear(coords, radiusKm = Infinity) {
    return getStores()
      .map((store) => ({ ...store, distanceKm: haversineKm(coords, { lat: store.latitude, lng: store.longitude }) }))
      .filter((store) => store.distanceKm <= radiusKm)
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }

  function storesByState(stateCode) {
    return getStores().filter((store) => store.stateCode === stateCode);
  }

  function searchStores(query, stores = getStores()) {
    const needle = normalizeText(query);
    if (!needle) return stores;
    return stores.filter((store) => normalizeText([
      store.displayName, store.name, store.city, store.municipality, store.address1, store.address2,
      store.state, store.stateCode, store.postalCode, store.storeCode
    ].join(' ')).includes(needle));
  }

  function uniqueStates() {
    const map = new Map();
    getStores().forEach((store) => map.set(store.stateCode, store.state));
    return [...map.entries()].map(([code, label]) => ({ code, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }

  async function geocodePostalCode(postalCode) {
    const cp = normalizeZip(postalCode);
    if (!/^\d{5}$/.test(cp)) throw new Error('INVALID_POSTAL_CODE');
    if (!window.google?.maps?.Geocoder) throw new Error('GOOGLE_MAPS_NOT_READY');

    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ address: `${cp}, México`, componentRestrictions: { country: 'MX', postalCode: cp } });
    const result = results?.[0];
    if (!result?.geometry?.location) throw new Error('POSTAL_CODE_NOT_FOUND');
    return { lat: result.geometry.location.lat(), lng: result.geometry.location.lng(), postalCode: cp };
  }

  function requestUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(Object.assign(new Error('GEOLOCATION_UNSUPPORTED'), { code: 0 }));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
        reject,
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  function getConfig() {
    return window.ShasaStoreLocatorConfig || {};
  }

  async function api(path, payload) {
    const config = getConfig();
    const base = (config.apiBase || 'https://macadamia.azurewebsites.net').replace(/\/$/, '');
    const response = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.status === 'error') {
      const error = new Error(data?.message || `HTTP_${response.status}`);
      error.response = data;
      throw error;
    }
    return data;
  }

  function updateStoreHeaderLabel(storeCode, storeData = null) {
    const labels = document.querySelectorAll(
      '[data-store-header-label]'
    );

    if (!labels.length) return;

    let label = '';

    /*
     * Primero intentamos usar el nombre devuelto por backend.
     */
    if (storeData) {
      label =
        storeData.displayName ||
        storeData.name ||
        storeData.store_name ||
        '';
    }

    /*
     * Si backend no trae nombre o su estructura cambia,
     * buscamos la tienda en shasa-stores.js por storeCode.
     */
    if (!label && storeCode) {
      const localStore = getStores().find(
        (store) => store.storeCode === storeCode
      );

      if (localStore) {
        label =
          localStore.name ||
          localStore.displayName ||
          '';
      }
    }

    if (!label) return;

    labels.forEach((element) => {
      element.textContent = label;
    });
  }

  async function getFavoriteStore() {
    const { customerId } = getConfig();

    if (!customerId) {
      return null;
    }

    try {
      const response = await api(
        '/api/v4/shasa/customer/get-store',
        {
          customer_shopify_id:
            String(customerId)
        }
      );

      const data = response?.data;

      if (!data?.store_code) {
        return null;
      }

      const favorite = {
        customerId:
          data.customer_shopify_id,

        storeCode:
          data.store_code,

        shopifyLocationId:
          data.shopify_location_id,

        shopifyLocationGid:
          data.shopify_location_gid,

        store:
          data.store || null
      };

      /*
       * Actualizamos el header con la favorita real.
       */
      updateStoreHeaderLabel(
        favorite.storeCode,
        favorite.store
      );

      return favorite;
    } catch (error) {
      /*
       * Customer sin favorita:
       * simplemente dejamos el texto original del header.
       */
      console.warn(
        '[StoreLocator] Could not load favorite store:',
        error
      );

      return null;
    }
  }

  async function saveFavoriteStore(storeCode) {
    const { customerId } = getConfig();
    if (!customerId) throw new Error('CUSTOMER_REQUIRED');
    return api('/api/v4/shasa/customer/store', {
      customer_shopify_id: String(customerId),
      store_code: storeCode
    });
  }

  async function getCurrentCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });
 
    if (!response.ok) {
      throw new Error('CART_FETCH_FAILED');
    }
 
    const cart = await response.json();
 
    if (!cart?.token) {
      throw new Error('CART_TOKEN_MISSING');
    }
 
    return cart;
  }
 
 
  function getGraphqlCartId(cartToken) {
    const token = String(cartToken || '').trim();
 
    if (!token) {
      throw new Error('CART_TOKEN_MISSING');
    }
 
    /*
     * MUY IMPORTANTE:
     * No quitar ?key=...
     *
     * Conservamos exactamente el token que devuelve /cart.js.
     */
    return token.startsWith('gid://shopify/Cart/')
      ? token
      : `gid://shopify/Cart/${token}`;
  }
 
 
  function getPickupHandle(favorite) {
    if (!favorite) return null;
 
    /*
     * Shopify acepta Location ID para Local Pickup.
     * Preferimos el ID numérico devuelto por backend.
     */
    if (favorite.shopifyLocationId) {
      return String(favorite.shopifyLocationId)
        .replace('gid://shopify/Location/', '')
        .trim();
    }
 
    /*
     * Fallback si backend solamente devuelve GID.
     */
    if (favorite.shopifyLocationGid) {
      return String(favorite.shopifyLocationGid)
        .replace('gid://shopify/Location/', '')
        .trim();
    }
 
    return null;
  }
 
 
  async function updatePickupPreferenceDirect(
    cartToken,
    pickupHandle
  ) {
    const config = getConfig();
 
    const shopDomain =
      config.shopDomain ||
      'shasamx.myshopify.com';
 
    const apiVersion =
      config.storefrontApiVersion ||
      '2026-07';
 
    const storefrontToken =
      config.storefrontToken;
 
    if (!storefrontToken) {
      throw new Error('STOREFRONT_TOKEN_MISSING');
    }
 
    if (!pickupHandle) {
      throw new Error('PICKUP_HANDLE_MISSING');
    }
 
    const cartId =
      getGraphqlCartId(cartToken);
 
    const query = `
      mutation UpdatePickupPreference(
        $cartId: ID!
        $buyerIdentity: CartBuyerIdentityInput!
      ) {
        cartBuyerIdentityUpdate(
          cartId: $cartId
          buyerIdentity: $buyerIdentity
        ) {
          cart {
            id
            checkoutUrl
 
            buyerIdentity {
              preferences {
                delivery {
                  deliveryMethod
                  pickupHandle
                }
              }
            }
          }
 
          userErrors {
            field
            message
          }
 
          warnings {
            code
            message
          }
        }
      }
    `;
 
    const variables = {
      cartId,
 
      buyerIdentity: {
        preferences: {
          delivery: {
            deliveryMethod: [
              'PICK_UP'
            ],
 
            pickupHandle: [
              String(pickupHandle)
            ]
          }
        }
      }
    };
 
    const response = await fetch(
      `https://${shopDomain}/api/${apiVersion}/graphql.json`,
      {
        method: 'POST',
 
        headers: {
          'Content-Type':
            'application/json',
 
          Accept:
            'application/json',
 
          'X-Shopify-Storefront-Access-Token':
            storefrontToken
        },
 
        body: JSON.stringify({
          query,
          variables
        })
      }
    );
 
    let data;
 
    try {
      data =
        await response.json();
    } catch {
      throw new Error(
        'STOREFRONT_INVALID_JSON_RESPONSE'
      );
    }
 
    if (!response.ok) {
      console.error(
        '[StoreLocator] Storefront HTTP error:',
        response.status,
        data
      );
 
      throw new Error(
        `STOREFRONT_HTTP_${response.status}`
      );
    }
 
    if (data?.errors?.length) {
      console.error(
        '[StoreLocator] Storefront GraphQL errors:',
        data.errors
      );
 
      throw new Error(
        data.errors[0]?.message ||
        'STOREFRONT_GRAPHQL_ERROR'
      );
    }
 
    const payload =
      data?.data
        ?.cartBuyerIdentityUpdate;
 
    if (
      payload?.userErrors?.length
    ) {
      console.error(
        '[StoreLocator] Buyer identity errors:',
        payload.userErrors
      );
 
      throw new Error(
        payload.userErrors[0]
          ?.message ||
        'BUYER_IDENTITY_UPDATE_FAILED'
      );
    }
 
    const returnedHandles =
      payload?.cart
        ?.buyerIdentity
        ?.preferences
        ?.delivery
        ?.pickupHandle ||
      [];
 
    const requestedHandle =
      String(pickupHandle);
 
    /*
     * No continuamos silenciosamente si Shopify
     * ni siquiera guardó nuestra preferencia.
     */
    if (
      !returnedHandles
        .map(String)
        .includes(requestedHandle)
    ) {
      console.error(
        '[StoreLocator] Shopify did not preserve requested pickup handle.',
        {
          requested:
            requestedHandle,
 
          returned:
            returnedHandles
        }
      );
 
      throw new Error(
        'PICKUP_HANDLE_NOT_PRESERVED'
      );
    }
 
    return {
      checkoutUrl:
        payload?.cart?.checkoutUrl ||
        null,
 
      pickupHandle:
        requestedHandle,
 
      returnedPickupHandles:
        returnedHandles,
 
      warnings:
        payload?.warnings || []
    };
  }
 
 
  async function syncCartPreference({
    requireFavorite = false
  } = {}) {
    const {
      customerId
    } = getConfig();
 
    if (!customerId) {
      return {
        skipped: 'guest'
      };
    }
 
    const [
      cart,
      favorite
    ] = await Promise.all([
      getCurrentCart(),
      getFavoriteStore()
    ]);
 
    if (!cart.item_count) {
      return {
        skipped:
          'empty_cart'
      };
    }
 
    if (!favorite) {
      if (requireFavorite) {
        throw new Error(
          'FAVORITE_STORE_MISSING'
        );
      }
 
      return {
        skipped:
          'no_favorite'
      };
    }
 
    const pickupHandle =
      getPickupHandle(favorite);
 
    if (!pickupHandle) {
      throw new Error(
        'SHOPIFY_LOCATION_ID_MISSING'
      );
    }
 
    /*
     * PRIMERA OPCIÓN:
     * Storefront API directamente.
     *
     * Esto nos devuelve checkoutUrl DEL MISMO CART.
     */
    try {
      const storefrontResult =
        await updatePickupPreferenceDirect(
          cart.token,
          pickupHandle
        );
 
      console.log(
        '[StoreLocator] Pickup preference confirmed by Shopify',
        {
          storeCode:
            favorite.storeCode,
 
          pickupHandle:
            storefrontResult.pickupHandle,
 
          returnedPickupHandles:
            storefrontResult
              .returnedPickupHandles
        }
      );
 
      return {
        ...storefrontResult,
 
        cartToken:
          cart.token,
 
        favorite
      };
 
    } catch (storefrontError) {
      console.error(
        '[StoreLocator] Direct Storefront preference failed:',
        storefrontError
      );
 
      /*
       * FALLBACK:
       * mantenemos tu integración backend actual.
       *
       * Así nunca rompemos checkout aunque haya
       * una falla temporal del Storefront API.
       */
      const backendResult =
        await api(
          '/api/v4/shasa/cart/update-buyer-identity',
          {
            customer_shopify_id:
              String(customerId),
 
            cart_id:
              cart.token
          }
        );
 
      console.warn(
        '[StoreLocator] Used backend fallback for pickup preference'
      );
 
      return {
        backendResult,
 
        checkoutUrl:
          backendResult
            ?.data
            ?.cart
            ?.checkoutUrl ||
 
          backendResult
            ?.data
            ?.checkoutUrl ||
 
          backendResult
            ?.checkoutUrl ||
 
          null,
 
        cartToken:
          cart.token,
 
        favorite,
 
        pickupHandle
      };
    }
  }
 
 
  async function checkoutWithPreferredStore() {
    /*
     * Evita doble click / doble request.
     */
    if (
      window
        .__shasaCheckoutPreferenceInFlight
    ) {
      return;
    }
 
    window
      .__shasaCheckoutPreferenceInFlight =
      true;
 
    try {
      const {
        customerId
      } = getConfig();
 
      /*
       * Guest:
       * checkout normal.
       */
      if (!customerId) {
        window.location.assign(
          '/checkout'
        );
 
        return;
      }
 
      const result =
        await syncCartPreference();
 
      /*
       * Si no tiene tienda favorita,
       * no bloqueamos checkout.
       */
      if (
        result?.skipped ===
          'no_favorite' ||
        result?.skipped ===
          'guest'
      ) {
        window.location.assign(
          '/checkout'
        );
 
        return;
      }
 
      if (
        result?.skipped ===
        'empty_cart'
      ) {
        window.location.assign(
          '/cart'
        );
 
        return;
      }
 
      /*
       * CLAVE:
       *
       * Si Shopify devuelve checkoutUrl
       * después de cartBuyerIdentityUpdate,
       * usamos ESA URL inmediatamente.
       */
      const checkoutUrl =
        result?.checkoutUrl ||
        '/checkout';
 
      console.log(
        '[StoreLocator] Going to checkout with preferred pickup',
        {
          pickupHandle:
            result?.pickupHandle,
 
          checkoutUrl
        }
      );
 
      window.location.assign(
        checkoutUrl
      );
 
    } catch (error) {
      /*
       * Nunca bloqueamos una venta.
       */
      console.error(
        '[StoreLocator] Preferred pickup checkout failed:',
        error
      );
 
      window.location.assign(
        '/checkout'
      );
 
    } finally {
      /*
       * Si por alguna razón navigation no sucede,
       * permitimos reintentar.
       */
      window.setTimeout(
        () => {
          window
            .__shasaCheckoutPreferenceInFlight =
            false;
        },
        4000
      );
    }
  }
 
 
  function mapUrl(context = {}) {
    const {
      locatorUrl =
        '/pages/localizador-de-tiendas'
    } = getConfig();
 
    const params =
      new URLSearchParams();
 
    Object.entries(
      context
    ).forEach(
      ([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== ''
        ) {
          params.set(
            key,
            String(value)
          );
        }
      }
    );
 
    return `${locatorUrl}${
      params.size
        ? `?${params}`
        : ''
    }`;
  }
 
 
  window.ShasaStoreLocator = {
    STATE_LABELS,
    normalizeStore,
    getStores,
    storesNear,
    storesByState,
    searchStores,
    uniqueStates,
    geocodePostalCode,
    requestUserLocation,
    getFavoriteStore,
    saveFavoriteStore,
    syncCartPreference,
    checkoutWithPreferredStore,
    mapUrl,
    normalizeZip
  };
 
 
  function loadFavoriteStoreForHeader() {
    const {
      customerId
    } = getConfig();
 
    if (!customerId) {
      return;
    }
 
    getFavoriteStore()
      .catch(
        (error) => {
          console.warn(
            '[StoreLocator] Header favorite store load failed:',
            error
          );
        }
      );
  }
 
 
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      loadFavoriteStoreForHeader,
      {
        once: true
      }
    );
 
  } else {
    loadFavoriteStoreForHeader();
  }
 
 
  /*
   * Mantener sincronizada la preferencia
   * cuando se abre el cart drawer.
   */
  let cartPreferenceSyncInFlight =
    false;
 
 
  document.addEventListener(
    'theme:cart-drawer:open',
    async () => {
      if (
        !getConfig()
          ?.customerId
      ) {
        return;
      }
 
      if (
        cartPreferenceSyncInFlight
      ) {
        return;
      }
 
      cartPreferenceSyncInFlight =
        true;
 
      try {
        const result =
          await syncCartPreference();
 
        console.log(
          '[StoreLocator] Cart preference synced',
          result
        );
 
      } catch (error) {
        console.error(
          '[StoreLocator] Cart preference sync failed:',
          error
        );
 
      } finally {
        cartPreferenceSyncInFlight =
          false;
      }
    }
  );
 
 
  /*
   * CHECKOUT INTERCEPTOR
   *
   * El paso importante:
   * sincronizamos Mi tienda JUSTO ANTES
   * de entrar a Checkout.
   */
  function findCheckoutControl(
    target
  ) {
    if (
      !target?.closest
    ) {
      return null;
    }
 
    return target.closest(
      [
        'button[name="checkout"]',
        'input[name="checkout"]',
        '[data-checkout-button]',
        'a[href="/checkout"]',
        'a[href^="/checkout?"]'
      ].join(',')
    );
  }
 
 
  document.addEventListener(
    'click',
    (event) => {
      const control =
        findCheckoutControl(
          event.target
        );
 
      if (!control) {
        return;
      }
 
      /*
       * Desde aquí NOSOTROS controlamos
       * cuándo ocurre el redirect.
       */
      event.preventDefault();
      event.stopPropagation();
 
      if (
        control instanceof
          HTMLButtonElement ||
        control instanceof
          HTMLInputElement
      ) {
        control.disabled =
          true;
      }
 
      checkoutWithPreferredStore();
    },
    true
  );
 
 
  /*
   * Cubre también checkout disparado
   * por submit/Enter y no solamente click.
   */
  document.addEventListener(
    'submit',
    (event) => {
      const submitter =
        event.submitter;
 
      const isCheckoutSubmit =
        submitter?.name ===
          'checkout' ||
 
        event.target
          ?.getAttribute?.(
            'action'
          ) ===
          '/checkout';
 
      if (
        !isCheckoutSubmit
      ) {
        return;
      }
 
      event.preventDefault();
      event.stopPropagation();
 
      checkoutWithPreferredStore();
    },
    true
  );
 
})();